import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, IconButton, Menu, MenuItem, Popover, Tab, Tabs, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChartIcon from '@mui/icons-material/BarChart';
import RepeatIcon from '@mui/icons-material/Repeat';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link, useHistory, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ProjectsService } from '../../services/dao/projects-service';
import { FindocsService } from '../../services/dao/findocs-service';
import { ReceiptsService } from '../../services/dao/receipts-service';

const projectsService = new ProjectsService();
const findocsService = new FindocsService();
const receiptsService = new ReceiptsService();

const fmtMoney = (v) => v != null ? `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;
const fmtDate = (d) => { try { return d ? format(new Date(d), 'MMM d, yyyy') : null; } catch { return null; } };

const getProcessingState = (doc) => doc?.meta?.processingState;
const isProcessing = (doc) => { const s = getProcessingState(doc); return !s || s === 'started'; };
const isFailed = (doc) => getProcessingState(doc) === 'failed';

function DocCard({ doc, type, onMove, onDelete, onReanalyze }) {
    const [anchor, setAnchor] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const history = useHistory();
    const processing = isProcessing(doc);
    const failed = isFailed(doc);
    const title = processing
        ? (doc?.meta?.fileName || 'Processing...')
        : type === 'findoc' ? (doc.institutionName || 'Statement') : (doc.vendorName || 'Receipt');
    const amount = !processing ? (type === 'findoc' ? doc.balance : doc.total) : null;
    const date = !processing ? fmtDate(type === 'findoc' ? doc.statementDate : doc.invoiceReceiptDate) : null;
    const detailPath = type === 'findoc' ? `/findocs/${doc._id}` : `/receipts/${doc._id}`;

    return (
        <Box sx={{ p: 2.5, borderRadius: 2.5, background: '#fff',
            border: failed ? '1px solid #FCA5A5' : '1px solid rgba(0,0,0,0.06)',
            cursor: processing ? 'default' : 'pointer', transition: 'all 0.2s',
            opacity: processing ? 0.85 : 1,
            '&:hover': processing ? {} : { boxShadow: '0 4px 20px rgba(0,0,0,0.06)' } }}
            onClick={() => !processing && history.push(detailPath)}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0, flex: 1 }}>
                    {processing ? (
                        <HourglassTopIcon sx={{ color: '#F59E0B', fontSize: '1.2rem', animation: 'spin 2s linear infinite',
                            '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                    ) : failed ? (
                        <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: '1.2rem' }} />
                    ) : type === 'findoc' ? (
                        <DescriptionIcon sx={{ color: '#1F2F42', fontSize: '1.2rem' }} />
                    ) : (
                        <ReceiptLongIcon sx={{ color: '#C89E57', fontSize: '1.2rem' }} />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>{title}</Typography>
                        {processing && <Typography variant="caption" sx={{ color: '#F59E0B' }}>Processing...</Typography>}
                        {failed && <Typography variant="caption" sx={{ color: '#EF4444' }}>Processing failed</Typography>}
                        {!processing && !failed && type === 'findoc' && doc.documentType && (
                            <Typography variant="caption" sx={{ color: doc.documentType === 'credit_card' ? '#92400E' : '#1E40AF', fontWeight: 500 }}>
                                {doc.documentType === 'credit_card' ? 'Credit Card' : 'Bank Statement'}
                            </Typography>
                        )}
                        {date && !processing && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{date}</Typography>}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {amount != null && (
                        <Chip label={fmtMoney(amount)} size="small"
                            sx={{ fontWeight: 600, fontSize: '0.75rem',
                                background: type === 'findoc' ? '#E8EDF2' : '#ECFDF5',
                                color: type === 'findoc' ? '#141F2E' : '#059669' }} />
                    )}
                    {processing && <CircularProgress size={16} sx={{ color: '#F59E0B', ml: 0.5 }} />}
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
                        <MoreVertIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Box>
            </Box>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={e => e.stopPropagation()}>
                <MenuItem onClick={() => { setAnchor(null); onMove(doc._id, type); }}>Move to Project</MenuItem>
                {type === 'findoc' && (
                    <MenuItem onClick={() => { setAnchor(null); onReanalyze(doc._id); }}>
                        <RefreshIcon sx={{ fontSize: '1rem', mr: 1 }} /> Reanalyze
                    </MenuItem>
                )}
                <MenuItem onClick={() => { setAnchor(null); setConfirmDelete(true); }} sx={{ color: '#EF4444' }}>
                    <DeleteIcon sx={{ fontSize: '1rem', mr: 1 }} /> Delete
                </MenuItem>
            </Menu>
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}
                PaperProps={{ sx: { borderRadius: 3 } }} onClick={e => e.stopPropagation()}>
                <DialogTitle>Delete {type === 'findoc' ? 'statement' : 'receipt'}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">This will permanently delete "{title}" and all associated files. This cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => { setConfirmDelete(false); onDelete(doc._id, type); }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export function BucketDetailScreen() {
    const { bucketName } = useParams();
    const projectId = bucketName;
    const history = useHistory();
    const [findocs, setFindocs] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [projectName, setProjectName] = useState('');
    const [tab, setTab] = useState(0);
    const [moveDialog, setMoveDialog] = useState(null);
    const [allProjects, setAllProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [snapshotOpen, setSnapshotOpen] = useState(true);
    const [catSampleAnchor, setCatSampleAnchor] = useState(null);
    const [catSampleName, setCatSampleName] = useState('');
    const [catSampleItems, setCatSampleItems] = useState([]);
    const [reanalyzeAllOpen, setReanalyzeAllOpen] = useState(false);
    const [reanalyzeAllLoading, setReanalyzeAllLoading] = useState(false);

    const load = (silent = false) => {
        if (!silent) setLoading(true);
        Promise.all([
            projectsService.getProjectContents(projectId),
            projectsService.getProjects(),
        ]).then(([contents, projectList]) => {
            if (contents?.body) {
                setFindocs(contents.body.findocs || []);
                setReceipts(contents.body.receipts || []);
            }
            if (projectList?.body) {
                setAllProjects(projectList.body.filter(p => p._id));
                const current = projectList.body.find(p => p._id === projectId);
                setProjectName(current?.name || (projectId === 'unassigned' ? 'Unassigned' : 'Project'));
            }
            if (!silent) setLoading(false);
        });
    };

    useEffect(() => load(), [projectId]);

    // Silent poll while any docs are processing
    useEffect(() => {
        const hasProcessing = [...findocs, ...receipts].some(d => isProcessing(d));
        if (!hasProcessing) return;
        const timer = setInterval(() => load(true), 8000);
        return () => clearInterval(timer);
    }, [findocs, receipts]);

    const handleMove = async () => {
        if (!moveDialog) return;
        const targetId = selectedProjectId || null;
        const fn = moveDialog.type === 'receipt' ? projectsService.assignReceipt : projectsService.assignFindoc;
        await fn(moveDialog.id, targetId);
        setMoveDialog(null);
        setSelectedProjectId('');
        load();
    };

    const handleDelete = async (id, type) => {
        if (type === 'findoc') await findocsService.deleteFindoc(id);
        else await receiptsService.deleteReceipt(id);
        load();
    };

    const handleReanalyze = async (findocId) => {
        await findocsService.reanalyze(findocId);
        load();
    };

    const handleReanalyzeAll = async () => {
        setReanalyzeAllLoading(true);
        await Promise.all(findocs.map(f => findocsService.reanalyze(f._id)));
        setReanalyzeAllOpen(false);
        setReanalyzeAllLoading(false);
        load();
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;

    const total = findocs.length + receipts.length;
    const uploadBase = projectId !== 'unassigned' ? '?projectId=' + projectId : '';

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <IconButton onClick={() => history.push('/projects')} size="small"><ArrowBackIcon /></IconButton>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{projectName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, ml: 5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {findocs.length} statements · {receipts.length} receipts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button component={Link} to={'/upload-findoc' + uploadBase} variant="outlined" size="small"
                            startIcon={<AddIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                            Add Statement
                        </Button>
                        <Button component={Link} to={'/upload-receipt' + uploadBase} variant="outlined" size="small"
                            startIcon={<AddIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                            Add Receipt
                        </Button>
                        {projectId !== 'unassigned' && findocs.length > 0 && (
                            <Button component={Link} to={`/projects/${projectId}/dashboard`} variant="outlined" size="small"
                                startIcon={<BarChartIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                                Insights
                            </Button>
                        )}
                        {findocs.length > 0 && (
                            <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
                                onClick={() => setReanalyzeAllOpen(true)}
                                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                                Reanalyze All
                            </Button>
                        )}
                    </Box>
                </Box>
            </motion.div>

            {total === 0 && (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <DescriptionIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>No documents yet</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                        Upload a document to get started.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button component={Link} to={'/upload-findoc' + uploadBase} variant="contained" startIcon={<AddIcon />}
                            sx={{ borderRadius: 2, textTransform: 'none' }}>Upload Statement</Button>
                        <Button component={Link} to={'/upload-receipt' + uploadBase} variant="outlined" startIcon={<AddIcon />}
                            sx={{ borderRadius: 2, textTransform: 'none' }}>Upload Receipt</Button>
                    </Box>
                </Box>
            )}

            {total > 0 && (
                <>
                    {/* Quick Snapshot */}
                    {(() => {
                        const allLI = findocs.flatMap(f => f.lineItems || []);
                        if (allLI.length === 0) return null;

                        // Top categories
                        const catMap = {};
                        allLI.forEach(li => {
                            const cat = li.category || 'Uncategorized';
                            const amt = Math.abs(li.amount || 0);
                            if (amt > 0 && li.transactionType === 'debit') catMap[cat] = (catMap[cat] || 0) + amt;
                        });
                        const topCats = Object.entries(catMap).sort((a, b) => b[1] - a[1]).slice(0, 6);

                        // Subscriptions
                        const byDesc = {};
                        allLI.forEach(li => {
                            if (!li.description || li.amount == null) return;
                            const key = li.description.toLowerCase().replace(/\s+/g, ' ').trim();
                            const amt = Math.abs(li.amount);
                            if (!byDesc[key]) byDesc[key] = { desc: li.description, amts: [], aiTagged: false };
                            byDesc[key].amts.push(amt);
                            if (li.isSubscription) byDesc[key].aiTagged = true;
                        });
                        const subs = Object.values(byDesc).filter(s => {
                            if (s.aiTagged) return true;
                            if (s.amts.length < 2) return false;
                            const avg = s.amts.reduce((a, b) => a + b, 0) / s.amts.length;
                            return avg > 0 && s.amts.every(a => Math.abs(a - avg) / avg < 0.15);
                        }).map(s => ({ desc: s.desc, avg: s.amts.reduce((a, b) => a + b, 0) / s.amts.length, count: s.amts.length }))
                        .sort((a, b) => b.avg * b.count - a.avg * a.count).slice(0, 5);

                        const totalSpend = Object.values(catMap).reduce((a, b) => a + b, 0);

                        return (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: 1 }}
                                    onClick={() => setSnapshotOpen(!snapshotOpen)}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                        Quick Snapshot
                                    </Typography>
                                    {snapshotOpen ? <ExpandLessIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                                </Box>
                                <Collapse in={snapshotOpen}>
                                    <Grid container spacing={2}>
                                        {/* Categories */}
                                        <Grid item xs={12} md={subs.length > 0 ? 7 : 12}>
                                            <Box sx={{ p: 2.5, borderRadius: 2.5, background: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <BarChartIcon sx={{ fontSize: '1rem', color: '#1F2F42' }} />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Top Categories</Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                                                        Total: {fmtMoney(totalSpend)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                    {topCats.map(([cat, amt]) => {
                                                        const pct = totalSpend > 0 ? (amt / totalSpend) * 100 : 0;
                                                        return (
                                                            <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', width: 140, gap: 0.25 }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.8rem', flex: 1 }} noWrap>{cat}</Typography>
                                                                    <IconButton size="small" sx={{ p: 0 }} onClick={(e) => {
                                                                        setCatSampleAnchor(e.currentTarget);
                                                                        setCatSampleName(cat);
                                                                        setCatSampleItems(allLI.filter(li => (li.category || 'Uncategorized') === cat).slice(0, 5));
                                                                    }}>
                                                                        <InfoOutlinedIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                                                                    </IconButton>
                                                                </Box>
                                                                <Box sx={{ flex: 1, height: 6, borderRadius: 3, background: '#F1F5F9', overflow: 'hidden' }}>
                                                                    <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: '#1F2F42' }} />
                                                                </Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem', minWidth: 60, textAlign: 'right' }}>
                                                                    {fmtMoney(amt)}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        </Grid>
                                        {/* Subscriptions */}
                                        {subs.length > 0 && (
                                            <Grid item xs={12} md={5}>
                                                <Box sx={{ p: 2.5, borderRadius: 2.5, background: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                        <RepeatIcon sx={{ fontSize: '1rem', color: '#C89E57' }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Recurring Charges</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                        {subs.map((s, i) => (
                                                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.8rem', display: 'block' }} noWrap>{s.desc}</Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                                                    <Chip label={`${s.count}x`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }} />
                                                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#DC2626' }}>
                                                                        {fmtMoney(s.avg)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Collapse>
                            </Box>
                        );
                    })()}

                    <Popover open={Boolean(catSampleAnchor)} anchorEl={catSampleAnchor}
                        onClose={() => setCatSampleAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                        <Box sx={{ p: 2, maxWidth: 300, minWidth: 200 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{catSampleName} — Samples</Typography>
                            {catSampleItems.length === 0 ? (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>No items</Typography>
                            ) : catSampleItems.map((li, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                                    <Typography variant="caption" noWrap sx={{ flex: 1, mr: 1 }}>{li.description || '—'}</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>{fmtMoney(li.amount)}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Popover>

                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' } }}>
                        <Tab label={`Statements (${findocs.length})`} icon={<DescriptionIcon sx={{ fontSize: '1.1rem' }} />} iconPosition="start" />
                        <Tab label={`Receipts (${receipts.length})`} icon={<ReceiptLongIcon sx={{ fontSize: '1.1rem' }} />} iconPosition="start" />
                    </Tabs>

                    {tab === 0 && (
                        <Grid container spacing={2}>
                            {findocs.map(f => (
                                <Grid item xs={12} sm={6} md={4} key={f._id}>
                                    <DocCard doc={f} type="findoc"
                                        onMove={(id, type) => setMoveDialog({ id, type })}
                                        onDelete={handleDelete}
                                        onReanalyze={handleReanalyze} />
                                </Grid>
                            ))}
                            {findocs.length === 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No statements yet</Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {tab === 1 && (
                        <Grid container spacing={2}>
                            {receipts.map(r => (
                                <Grid item xs={12} sm={6} md={4} key={r._id}>
                                    <DocCard doc={r} type="receipt"
                                        onMove={(id, type) => setMoveDialog({ id, type })}
                                        onDelete={handleDelete} />
                                </Grid>
                            ))}
                            {receipts.length === 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No receipts yet</Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </>
            )}

            {/* Move to project dialog */}
            <Dialog open={reanalyzeAllOpen} onClose={() => !reanalyzeAllLoading && setReanalyzeAllOpen(false)}
                PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>Reanalyze All Statements</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        This will reanalyze {findocs.length} statement{findocs.length !== 1 ? 's' : ''} in this bucket.
                        Each document will be re-processed by AI, which may take several minutes. Continue?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setReanalyzeAllOpen(false)} disabled={reanalyzeAllLoading}>Cancel</Button>
                    <Button variant="contained" onClick={handleReanalyzeAll} disabled={reanalyzeAllLoading}
                        startIcon={reanalyzeAllLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}>
                        {reanalyzeAllLoading ? 'Submitting…' : 'Reanalyze All'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!moveDialog} onClose={() => setMoveDialog(null)} PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>Move to Project</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                        <Chip label="Unassigned" size="small"
                            onClick={() => setSelectedProjectId('')}
                            color={selectedProjectId === '' ? 'primary' : 'default'}
                            variant={selectedProjectId === '' ? 'filled' : 'outlined'} />
                        {allProjects.filter(p => p._id !== projectId).map(p => (
                            <Chip key={p._id} label={p.name} size="small"
                                onClick={() => setSelectedProjectId(p._id)}
                                color={selectedProjectId === p._id ? 'primary' : 'default'}
                                variant={selectedProjectId === p._id ? 'filled' : 'outlined'} />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMoveDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={handleMove}>Move</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
